import {useCallback, useState} from 'react';

import notifications from 'notifications';
import * as ImportUtil from 'util/ImportUtil';

import {FileMessageBlock} from './RoutineTypes';

interface UseRoutineFileUploadResult {
  uploadFile: (file: File) => Promise<void>;
  isUploading: boolean;
}

/**
 * Uploads a single file to a temporary S3 location and reports it as a not-yet-finalized
 * file block (carrying `s3_location`). The block is finalized into a permanent attachment
 * by the backend when the routine is saved, so nothing is persisted on upload.
 */
export default function useRoutineFileUpload(
  onFileUploaded: (fileBlock: FileMessageBlock) => void
): UseRoutineFileUploadResult {
  const [isUploading, setIsUploading] = useState(false);

  const uploadFile = useCallback(
    async (file: File) => {
      setIsUploading(true);
      try {
        // Wrap in Promise.resolve() because ImportUtil.upload returns a jQuery Deferred.
        const s3Location = await Promise.resolve(ImportUtil.upload(file));
        if (s3Location == null || s3Location === '') {
          throw new Error('File upload did not return an S3 location');
        }
        onFileUploaded({
          type: 'file',
          s3_location: s3Location,
          filename: file.name,
          media_type: file.type,
        });
        // Upload failures are surfaced to the user via a notification rather than rethrown,
        // since this is invoked fire-and-forget from the file picker's onChange handler.
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (error) {
        notifications.notifyError(error);
      } finally {
        setIsUploading(false);
      }
    },
    [onFileUploaded]
  );

  return {uploadFile, isUploading};
}
